<script setup lang="ts">
import { z } from 'zod'
import type { Form, FormSubmitEvent } from '#ui/types'
import { useAuthStore } from '~/store/auth'

defineOptions({
	name: 'ModalSignUp',
})

interface Props {
	modelValue?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: false,
})

const { t: $t, locale } = useI18n()
const { isReady, login } = useAuth()
const authStore = useAuthStore()
const guestScanData = useGuestScanData()
const route = useRoute()

const emit = defineEmits(['update:modelValue', 'closeAuthGoogle'])
const localePath = useLocalePath()

const schema = z.object({
	full_name: z.string().min(3, $t('First name is required')),
	email: z.string().email($t('Email address is required')),
})

type Schema = z.output<typeof schema>

const state = reactive<Schema>({
	full_name: '',
	email: '',
})

const form = ref<Form<Schema>>()
const loading = ref<boolean>(false)

const labelInit = 'text-neutral-50 text-base font-semibold'

const labelClassRed = {
	base: labelInit,
	required: `after:content-['*'] after:ms-0.5 after:text-destructive-500`,
}

const isOpen = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
})

const isFormValid = computed(() => schema.safeParse(state).success)

const handleEmailAlreadyActivated = async (contentData: any) => {
	if (guestScanData.hasPendingScanData()) {
		guestScanData.mergePendingScanMeta({ authMethod: 'login' })
	}
	saveLastInternalPath(route.fullPath)
	const response = await authStore.login(contentData)
	loading.value = false
	if (response && !response?.success) {
		const errors = Object.keys(response.errors)
		form.value!.setErrors(
			errors.map((err) => ({
				message: response.errors[err][0],
				path: err,
			})),
		)
		return
	}
	handleAfterAuth(contentData.email)
	authStore.SET_STATE_AUTH({
		type: 'type_modal_verify_email',
		value: 'login',
	})
}

const handleEmailAlreadyUsed = async (contentData: any) => {
	saveLastInternalPath(route.fullPath)
	const response = await authStore.resendActivation(contentData)

	if (response && !response?.success) {
		const emailErrors = response?.errors?.email
		if (Array.isArray(emailErrors)) {
			const isEmailInUse = emailErrors.includes('This account is already activated.')

			if (isEmailInUse) {
				handleEmailAlreadyActivated(contentData)
				return
			}
		}
		loading.value = false

		const errors = Object.keys(response.errors)
		form.value!.setErrors(
			errors.map((err) => ({
				message: response.errors[err][0],
				path: err,
			})),
		)
		return
	}

	handleAfterAuth(contentData.email)
	authStore.SET_STATE_AUTH({
		type: 'type_modal_verify_email',
		value: 'sign_up',
	})
}

const onSubmit = async (event: FormSubmitEvent<Schema>) => {
	form.value!.clear()
	loading.value = true
	if (guestScanData.hasPendingScanData()) {
		guestScanData.mergePendingScanMeta({ authMethod: 'signup' })
	}
	saveLastInternalPath(route.fullPath)
	const contentData = {
		first_name: event.data.full_name,
		email: event.data.email,
		lang: locale.value,
		redirect: route.fullPath,
	}

	const response = await authStore.register(contentData)
	if (response && !response?.success) {
		const emailErrors = response?.errors?.email
		if (Array.isArray(emailErrors)) {
			const isEmailInUse = emailErrors.includes('This email is already in use.')

			if (isEmailInUse) {
				const contentDataUsed = { email: event.data.email, lang: locale.value, redirect: route.fullPath }
				handleEmailAlreadyUsed(contentDataUsed)
				return
			}
		}

		loading.value = false
		const errors = Object.keys(response.errors)
		form.value!.setErrors(
			errors.map((err) => ({
				message: response.errors[err][0],
				path: err,
			})),
		)
		return
	}

	loading.value = false

	handleAfterAuth(event.data.email)

	authStore.SET_STATE_AUTH({
		type: 'type_modal_verify_email',
		value: 'sign_up',
	})
}

const handleAfterAuth = (email: string) => {
	authStore.SET_STATE_AUTH({
		type: 'email_to_verify',
		value: email,
	})
	authStore.SET_STATE_AUTH({
		type: 'showModalSignUp',
		value: false,
	})
	authStore.SET_STATE_AUTH({
		type: 'showModalVerifyEmail',
		value: true,
	})
}

const signUpGoogle = async () => {
	if (!isReady.value) return

	try {
		if (guestScanData.hasPendingScanData()) {
			guestScanData.mergePendingScanMeta({ authMethod: 'google' })
		}
		await login()
		authStore.SET_STATE_AUTH({ type: 'showModalLogin', value: false })
		emit('closeAuthGoogle')
	} catch (error) {
		console.error('Sign up error:', error)
	}
}

const openLogin = () => {
	authStore.SET_STATE_AUTH({
		type: 'showModalSignUp',
		value: false,
	})
	authStore.SET_STATE_AUTH({
		type: 'showModalLogin',
		value: true,
	})
}
</script>

<template>
	<UModal
		v-model="isOpen"
		:transition="false"
		:ui="{
			overlay: {
				background: 'bg-black/50',
			},
			width: 'w-full sm:max-w-xl',
			container: 'items-center',
			padding: 'p-4 sm:p-4',
			background: 'bg-secondary-800 sm:bg-secondary-700',
			rounded: 'rounded-2xl',
		}"
	>
		<div class="rounded-2xl p-7 smm:p-12 w-full max-w-md sm:max-w-xl space-y-8 mx-auto">
			<!-- Title -->
			<slot name="header">
				<div class="text-neutral-50 space-y-2.5 text-center">
					<h1 class="text-xl sm:text-3xl font-bold">{{ $t('Join') }} Luckywise!</h1>

					<p class="mb-8 bg-clip-text bg-gradient-to-bl from-[#4dd853] from-10% to-[#233034] to-80%">
						{{ $t('Already have an account') }}?
						<button
							class="text-transparent sm:font-bold"
							@click="openLogin"
						>
							{{ $t('Login') }}
						</button>
					</p>
				</div>
			</slot>

			<UForm
				ref="form"
				:schema="schema"
				:state="state"
				class="space-y-6"
				@submit="onSubmit"
			>
				<!-- Name Fields -->
				<UFormGroup
					name="full_name"
					:label="$t('Your name')"
					required
					:ui="{
						label: labelClassRed,
						error: 'text-destructive-800',
					}"
				>
					<UInput
						v-model="state.full_name"
						type="text"
						:placeholder="$t('Your name here')"
						color="primary"
						variant="outline"
						class="w-full"
						input-class="text-neutral-500 placeholder-neutral-800 ring-secondary-600 bg-secondary-900 focus:ring-1 min-h-10 text-base placeholder:text-sm"
						required
					/>
				</UFormGroup>

				<!-- Email Field -->
				<UFormGroup
					name="email"
					:label="$t('Your mail address')"
					required
					:ui="{
						label: labelClassRed,
						error: 'text-destructive-800',
					}"
				>
					<UInput
						v-model="state.email"
						type="email"
						placeholder="youremail@email.com"
						color="primary"
						variant="outline"
						class="w-full"
						input-class="text-neutral-500 placeholder-neutral-800 ring-secondary-600 bg-secondary-900 focus:ring-1 min-h-10 text-base placeholder:text-sm"
						required
					/>
				</UFormGroup>

				<!-- Submit Button -->
				<UButton
					:disabled="!isFormValid"
					type="submit"
					:color="isFormValid ? 'primary' : 'white'"
					:variant="isFormValid ? 'solid' : 'soft'"
					:loading="loading"
					:label="$t('Enter')"
					class="rounded-full w-full py-2 text-base flex justify-center border-none"
					:class="
						isFormValid
							? 'bg-primary-500 hover:bg-primary-600 text-white'
							: 'text-neutral-700 cursor-not-allowed bg-white/[.08] hover:bg-white/[.10]'
					"
				/>
			</UForm>
			<p class="text-center text-neutral-50 text-sm max-w-sm mx-auto">
				{{ $t('By continuing, you agree to') }} Luckywise
				<NuxtLink
					:to="localePath('/terms-of-service')"
					class="bg-gradient-green bg-clip-text text-transparent"
				>
					{{ $t('Terms of service') }}
				</NuxtLink>
				{{ $t('and') }}
				<NuxtLink
					:to="localePath('/privacy-policy')"
					class="bg-gradient-green bg-clip-text text-transparent"
				>
					{{ $t('Privacy Policy') }}
				</NuxtLink>
			</p>
			<!-- Divider -->
			<div class="relative">
				<div class="absolute inset-0 flex items-center">
					<div class="w-full border-t border-[#040308]/[.4] smm:border-white/[.08]" />
				</div>
				<div class="relative flex justify-center text-sm">
					<span class="lowercase sm:capitalize px-4 bg-secondary-800 sm:bg-secondary-700 text-neutral-100">{{
						$t('Or')
					}}</span>
				</div>
			</div>

			<!-- Social Login -->
			<div class="text-neutral-100 text-center space-y-4">
				<p class="leading-6 text-sm sm:text-base">{{ $t('Register with') }}:</p>
				<UButton
					:disabled="!isReady"
					color="white"
					variant="soft"
					label="Google"
					icon="i-gg:google"
					class="rounded-full smm:rounded-lg w-full py-2.5 text-base flex justify-center bg-white/[.08] hover:bg-white/[.10] border border-white/[.06]"
					@click="signUpGoogle"
				/>
			</div>
		</div>
	</UModal>
</template>
